<template>
  <div class="password-modal-overlay" v-if="show">
    <div class="password-modal">
      <div class="modal-header">
        <h2>🔐 Password Change Required</h2>
        <p class="subtitle">Please set a new password to continue</p>
      </div>
      
      <form @submit.prevent="changePassword" class="password-form">
        <div class="form-group">
          <label>Current Password</label>
          <input 
            type="password" 
            v-model="currentPassword" 
            placeholder="Enter current password"
            required 
          />
        </div>
        
        <div class="form-group">
          <label>New Password</label>
          <input 
            type="password" 
            v-model="newPassword" 
            placeholder="Enter new password (min 8 characters)"
            required 
            minlength="8"
          />
        </div>
        
        <div class="form-group">
          <label>Confirm New Password</label>
          <input 
            type="password" 
            v-model="confirmPassword" 
            placeholder="Confirm new password"
            required 
          />
        </div>
        
        <div class="error-message" v-if="errorMessage">
          {{ errorMessage }}
        </div>
        
        <div class="success-message" v-if="successMessage">
          {{ successMessage }}
        </div>
        
        <button type="submit" class="submit-btn" :disabled="loading">
          {{ loading ? 'Changing Password...' : 'Change Password' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useAuthStore } from '@/stores/auth_store';
import axios from 'axios';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['password-changed', 'close']);

const authStore = useAuthStore();

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const successMessage = ref('');
const loading = ref(false);

const changePassword = async () => {
  errorMessage.value = '';
  successMessage.value = '';
  
  if (newPassword.value.length < 8) {
    errorMessage.value = 'New password must be at least 8 characters';
    return;
  }
  
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Passwords do not match';
    return;
  }
  
  loading.value = true;
  
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/api/v1/passwords/change`,
      {
        current_password: currentPassword.value,
        new_password: newPassword.value,
        new_password_confirmation: confirmPassword.value
      },
      {
        headers: {
          'Authorization': `Bearer ${authStore.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    successMessage.value = 'Password changed successfully!';
    
    // Clear the force_password_change flag in store
    if (authStore.user) {
      authStore.user.force_password_change = false;
    }
    
    setTimeout(() => {
      emit('password-changed');
    }, 1500);
    
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Failed to change password';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.password-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.password-modal {
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  text-align: center;
  margin-bottom: 30px;
}

.modal-header h2 {
  color: #333;
  margin-bottom: 10px;
  font-size: 24px;
}

.modal-header .subtitle {
  color: #666;
  font-size: 14px;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
  font-size: 14px;
}

.form-group input {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.error-message {
  background: #fee2e2;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.success-message {
  background: #dcfce7;
  color: #16a34a;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
